import React, { useContext, useEffect, useMemo, useState } from 'react';
import { Avatar, Badge, Button, Layout, Menu } from 'antd';
import { Link, useLocation } from 'react-router-dom';
import {
  DashboardOutlined,
  UserOutlined,
  SettingOutlined,
  FileOutlined,
  BarChartOutlined,
  LogoutOutlined,
  EnvironmentOutlined,
  AppstoreOutlined,
  ShoppingCartOutlined,
  TeamOutlined,
  MessageOutlined,
  ContactsOutlined, 
  ShopOutlined,
  LeftOutlined,
  RightOutlined,
} from '@ant-design/icons';
import styled from 'styled-components';
import { useAuth } from '../context/AuthContext';
import axiosInstance from '@api/axiosInstance';
import { ClientContext } from '@context/ClientContext';
import { useAppConfig } from '../context/AppConfigContext';

const { Sider } = Layout;

const siderStyle = {
  height: '100dvh',
  position: 'sticky',
  insetInlineStart: 0,
  top: 0,
};

const SiderWrapper = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  max-width: 200px;
  background: #15151f;
`;

const StyledMenu = styled(Menu)`
  background-color: transparent !important;
  color: #d0d5dd !important;
  flex-direction: column;
  flex: 1;
  overflow: visible;
  border-inline-end: 0 !important;
  padding: 4px 8px;
  
  .ant-menu-item,
  .ant-menu-submenu-title {
    color: #d0d5dd !important;
    border-radius: 7px;
    height: 34px !important;
    line-height: 34px !important;
    margin: 2px 0 !important;
    width: 100%;
  }

  .ant-menu-item:hover,
  .ant-menu-submenu-title:hover {
    background: rgba(255, 255, 255, 0.06) !important;
    color: #ffffff !important;
  }

  .ant-menu-item-selected {
    background-color: #f97316 !important;
    color: #ffffff !important;
    font-weight: 600;
  }

  .ant-menu-sub {
    background: transparent !important;
  }

  .ant-menu-sub .ant-menu-item {
    padding-left: 36px !important;
  }
`;

const MessageBadge = styled(Badge)`
  .ant-badge-count {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    line-height: 14px !important;
  }

  .ant-scroll-number-only,
  .ant-scroll-number-only-unit {
    height: 14px !important;
    line-height: 14px !important;
  }
`;

const StyledSider = styled(Sider)`
  overflow: hidden;
  background-color: #15151f !important;
  display: flex;
  height: 100dvh;
  z-index: 1000;
  left: 0;
  top: 0;
`;

const Logo = styled.div`
  padding: 10px 12px 8px;
  text-align: center;
  background-color: #15151f;

  img {
    display: block;
    max-width: 142px;
    height: auto;
    margin: 0 auto;
  }
`;

const LogoutButton = styled.button`
  background-color: transparent;  
  border: none;
  color: inherit;
  cursor: pointer;
  text-align: left;
  width: 100%;
  padding: 0;
  font-family: inherit;
  font-size: inherit;
  
  &:hover {
    background-color: transparent;
    color: red;
  }
`;

const UserInfo = styled.div`
  padding: 10px;
  color: white;
  text-align: left;
  margin-top: auto;
  width: 100%;
  border-top: 1px solid rgba(255, 255, 255, 0.08);
  background: #171722;

  div {
    font-size: 12px;
    color: #d0d5dd;
  }
`;

const MenuWrapper = styled.div`
  flex: 1;
  overflow-y: auto;
  min-height: 0;
  
  /* SCROLLBAR CUSTOM STYLES (Webkit browsers only) */
  &::-webkit-scrollbar {
    width: 8px;
  }

  &::-webkit-scrollbar-track {
    background: #15151f;
  }

  &::-webkit-scrollbar-thumb {
    background-color: #444;
    border-radius: 4px;
    border: 2px solid #15151f;
  }

  &::-webkit-scrollbar-thumb:hover {
    background-color: #666;
  }

  /* Firefox custom scrollbar */
  scrollbar-width: thin;
  scrollbar-color: #444 #15151f;
`;

const UserPanel = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
  min-width: 0;
`;

const UserMeta = styled.div`
  min-width: 0;
  flex: 1;

  .name {
    color: #ffffff;
    font-weight: 600;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .detail {
    color: #98a2b3;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
`;

const SidebarToggle = styled(Button)`
  position: fixed;
  top: 50%;
  transform: translateY(-50%);
  z-index: 1100;
  width: 24px;
  height: 48px;
  padding: 0;
  border-radius: 0 8px 8px 0;
  border-left: 0;
  background: #15151f;
  color: #ffffff;
  box-shadow: 0 4px 12px rgba(15, 23, 42, 0.22);

  &:hover,
  &:focus {
    background: #1f1f2d !important;
    color: #ffffff !important;
  }
`;

function Sidebar({ collapsed = false, onToggle }) {
  const { appConfig } = useAppConfig();
  const { logout, user } = useAuth(); 
  const location = useLocation();
  const [ unreadMessagesCount, setUnreadMessagesCount ] = useState(0);
  const { client, setClient } = useContext(ClientContext);
  const [ clientLocalStorage, setClientLocalStorage ] = useState(localStorage.getItem("selectedClient") ? JSON.parse(localStorage.getItem("selectedClient")) : undefined);

  useEffect(() => {
    if (clientLocalStorage) {
      setClient(JSON.parse(localStorage.getItem("selectedClient")));
    } 
  }, [clientLocalStorage, setClient]);

  useEffect(() => {
    const fetchUnreadMessages = async () => {
      try {
        const response = await axiosInstance.get('/messages/get/unreadMessagesList');
        let sum = 0;
        response.data.forEach((item) => {
          sum++;
        })
        setUnreadMessagesCount(sum);
      } catch (error) {
        console.error('Błąd podczas pobierania nieprzeczytanych wiadomości:', error);
      }
    };
    fetchUnreadMessages();

    window.addEventListener('quickfox:messages-updated', fetchUnreadMessages);

    return () => {
      window.removeEventListener('quickfox:messages-updated', fetchUnreadMessages);
    };
  }, []);

  const isAdmin = user?.is_admin === true || user?.role === 'admin';
  const selectedKeys = useMemo(() => {
    const path = location.pathname;
    if (path.startsWith('/messages')) return ['messages'];
    if (path.startsWith('/clients')) return ['2'];
    if (path.startsWith('/expositions')) return ['3'];
    if (path.startsWith('/locations')) return ['4'];
    if (path.startsWith('/expositors')) return ['5'];
    if (path.startsWith('/orders')) return ['6'];
    if (path.startsWith('/trade')) return ['trade'];
    if (path.startsWith('/products')) return ['7'];
    if (path.startsWith('/reports')) return ['8'];
    if (path.startsWith('/visits/planner')) return ['visits-planner'];
    if (path.startsWith('/visits')) return ['visits'];
    if (path.startsWith('/settings/users')) return ['settings-users'];
    if (path.startsWith('/settings/document-types')) return ['settings-document-types'];
    if (path.startsWith('/settings/document-statuses')) return ['settings-document-statuses'];
    if (path.startsWith('/settings/change-newsletter')) return ['settings-change-newsletter'];
    if (path.startsWith('/settings/configuration')) return ['settings-configuration'];
    if (path.startsWith('/representatives/plans')) return ['rep-plans'];
    if (path.startsWith('/representatives/hierarchy')) return ['rep-hierarchy'];
    if (path.startsWith('/representatives/settings')) return ['rep-settings'];
    return ['1'];
  }, [location.pathname]);

  const handleLogout = () => {
    logout();
  };

  const tradeMenuItem = appConfig.features?.tradeDocuments
    ? {
        label: <Link to="/trade">Handel</Link>,
        key: 'trade',
        icon: <ShopOutlined />,
      }
    : null;

  // Zbuduj podstawowe elementy menu dostępne dla wszystkich
  const baseItems = !client ? [
    { label: <Link to="/">Dashboard</Link>, key: '1', icon: <DashboardOutlined /> },
    {
      label: <Link to='/messages'>Wiadomości</Link>,
      key: 'messages',
      icon: (
        <MessageBadge count={unreadMessagesCount} size="small" offset={[4, -2]}>
          <MessageOutlined style={{ color: 'inherit' }} />
        </MessageBadge>
      ),
    },
    { label: <Link to="/clients">Klienci</Link>, key: '2', icon: <UserOutlined /> },
    ...(tradeMenuItem ? [tradeMenuItem] : []),
    {
      label: 'Ekspozycja',
      key: 'sub1',
      icon: <AppstoreOutlined />,
      children: [
        { label: <Link to="/expositions">Ekspozycja</Link>, key: '3', icon: <FileOutlined /> },
        { label: <Link to="/locations">Lokalizacje</Link>, key: '4', icon: <EnvironmentOutlined /> },
        { label: <Link to="/expositors">Ekspozytory</Link>, key: '5', icon: <FileOutlined /> },
        { label: <Link to="/orders">Zlecenia</Link>, key: '6', icon: <ShoppingCartOutlined /> },
      ],
    },
    { label: <Link to="/products">Produkty</Link>, key: '7', icon: <FileOutlined /> },
    { label: <Link to="/reports">Raporty</Link>, key: '8', icon: <BarChartOutlined /> },
    {
      label: <Link to="/visits" style={{ color: 'inherit' }}>Wizyty</Link>,
      key: 'visits',
      icon: <ContactsOutlined />,
      children: [
        { label: <Link to="/visits/planner">Planer wizyt</Link>, key: 'visits-planner' },
      ],
    },
    { 
      label: <LogoutButton onClick={handleLogout}><LogoutOutlined style={{marginRight: '10px'}} />Wyloguj</LogoutButton>,
      key: '13'
    },
  ] : [
    { label: <Link to="/">Dashboard</Link>, key: '1', icon: <DashboardOutlined /> },
    ...(tradeMenuItem ? [tradeMenuItem] : []),
    /* { label: <Link to='/messages'>Wiadomości: {unreadMessagesCount}</Link>, key: 'messages', icon: <MessageOutlined/>}, */
    /* { label: <Link to="/clients">Klienci</Link>, key: '2', icon: <UserOutlined /> }, */
    { label: <Link to="/expositions">Ekspozycja</Link>, key: '3', icon: <FileOutlined /> },
    { label: <Link to="/orders">Zlecenia</Link>, key: '6', icon: <ShoppingCartOutlined /> },
    { label: <Link to="/expositors">Ekspozytory</Link>, key: '5', icon: <FileOutlined /> },
    { label: <Link to="/products">Produkty</Link>, key: '7', icon: <FileOutlined /> },
    /* { label: <Link to="/reports">Raporty</Link>, key: '8', icon: <BarChartOutlined /> }, */
    { 
      label: <LogoutButton onClick={handleLogout}><LogoutOutlined style={{marginRight: '10px'}} />Wyloguj</LogoutButton>,
      key: '13'
    },
  ];
  
  // Dodaj elementy menu administratora, jeśli użytkownik ma uprawnienia
  const items = isAdmin 
    ? [
        ...baseItems.slice(0, -1), // Wszystko oprócz ostatniego elementu (Logout)
        {
          label: 'Ustawienia',
          key: 'sub2',
          icon: <SettingOutlined />,
          children: [
            { label: <Link to="/settings/users">Użytkownicy i uprawnienia</Link>, key: 'settings-users' },
            { label: <Link to="/settings/document-types">Typy dokumentów</Link>, key: 'settings-document-types' },
            { label: <Link to="/settings/document-statuses">Statusy dokumentów</Link>, key: 'settings-document-statuses' },
            { label: <Link to="/settings/change-newsletter">Powiadomienia o zmianach</Link>, key: 'settings-change-newsletter' },
            { label: <Link to="/settings/configuration">Konfiguracja</Link>, key: 'settings-configuration' },
          ],
        },
        {
          label: 'Przedstawiciele',
          key: 'representatives',
          icon: <TeamOutlined />,
          children: [
            { label: <Link to="/representatives/plans">Plany</Link>, key: 'rep-plans' },
            { label: <Link to="/representatives/hierarchy">Hierarchia</Link>, key: 'rep-hierarchy' },
            { label: <Link to="/representatives/settings">Ustawienia</Link>, key: 'rep-settings' },
          ],
          },

        baseItems[baseItems.length - 1] // Dodaj Logout na końcu
      ]
    : baseItems;

  const sidebarWidth = collapsed ? 0 : 200;

  return (
    <StyledSider
      style={{
        ...siderStyle,
        flex: `0 0 ${sidebarWidth}px`,
        maxWidth: sidebarWidth,
        minWidth: sidebarWidth,
        width: sidebarWidth,
      }}
      width={sidebarWidth}
      collapsible={false}
    >
      <SidebarToggle
        icon={collapsed ? <RightOutlined /> : <LeftOutlined />}
        onClick={onToggle}
        size="small"
        style={{ left: sidebarWidth }}
        title={collapsed ? 'Pokaż menu' : 'Ukryj menu'}
        aria-label={collapsed ? 'Pokaż menu boczne' : 'Ukryj menu boczne'}
        aria-expanded={!collapsed}
      />
      <SiderWrapper
        aria-hidden={collapsed}
        style={{ visibility: collapsed ? 'hidden' : 'visible' }}
      >
      <Logo>
        <img src={appConfig.logoUrl} alt={appConfig.appName} />
      </Logo>
      <MenuWrapper>
      <StyledMenu
        theme="dark"
        mode="inline"
        selectedKeys={selectedKeys}
        defaultOpenKeys={['trade', 'sub1', 'visits', 'sub2', 'representatives']}
        items={items}
      />
      </MenuWrapper>
      {user && (
        <UserInfo>
  {(client) ? (
    <>
      <div style={{ display: "flex", flexDirection: "column" }}>
        <span>
          Wybrany klient: {client?.company_name}
        </span>
        <Button size="small" onClick={() => {
          setClient(undefined);
          setClientLocalStorage(undefined);
          localStorage.removeItem("selectedClient");
          
        }} style={{ marginTop: 8, opacity: 0.9, width: '100%' }}>Wyczyść</Button>
      </div>
    </>
  ) : (
    <UserPanel>
      <Avatar size={30} style={{ background: '#f97316', flex: '0 0 auto' }}>
        {`${user.first_name?.[0] || ''}${user.last_name?.[0] || ''}`.toUpperCase() || 'Q'}
      </Avatar>
      <UserMeta>
        <div className="name">{user.first_name} {user.last_name}</div>
        <div className="detail">{user.email}</div>
        <div className="detail">{user.role}</div>
      </UserMeta>
    </UserPanel>
  )}
</UserInfo>
      )}
      </SiderWrapper>
    </StyledSider>
  );
}

export default Sidebar;
